/**
 * This should replace the existing useGraphDataMaker hook.
 */

import { ChartDataPoint } from 'graphql/generated';
import moment from 'moment';
import { useMemo } from 'react';

export type ChartDataPointWithDateRange = ChartDataPoint & {
  dateRange: [Date, Date];
};

export type GraphDataMakerProps = {
  dailyChartData: ChartDataPoint[];
  dateRange?: [Date, Date];
  convertToLocalTimezone?: boolean;
  groupValueAggregationType?: 'avg' | 'sum';

  /**
   * If groupValueAggregationType is avg, and we want to avg on an external set of data,
   * we can pass in the daily data points for that set here.
   *
   * If this is not provided, we will avg on the count of days in the group.
   */
  dailyChartDataForAvg?: ChartDataPoint[];
};

export const createDateRangesForGraph = (startDate: Date, endDate: Date) => {
  const start = moment(startDate);
  const end = moment(endDate);
  const diffDays = end.diff(start, 'days');

  type DateRange = {
    startDate: Date;
    endDate: Date;
    label: string;
  };

  // For years (> 365 days)
  if (diffDays > 365) {
    const ranges: DateRange[] = [];
    const currentDate = start.clone().startOf('year');
    while (currentDate.isBefore(end)) {
      ranges.push({
        startDate: currentDate.toDate(),
        endDate: currentDate.clone().endOf('year').toDate(),
        label: currentDate.format('YYYY'),
      });
      currentDate.add(1, 'year');
    }
    return ranges;
  }

  // For months (> 31 days)
  if (diffDays > 31) {
    const ranges: DateRange[] = [];
    const currentDate = start.clone().startOf('month');
    while (currentDate.isBefore(end)) {
      ranges.push({
        startDate: currentDate.toDate(),
        endDate: currentDate.clone().endOf('month').toDate(),
        label: currentDate.format('MMM YYYY'),
      });
      currentDate.add(1, 'month');
    }
    return ranges;
  }

  // For weeks (> 9 days)
  // The graph can easily render 9 days, so we use 9 days as the cutoff
  if (diffDays > 9) {
    const ranges: DateRange[] = [];
    const currentDate = start.clone().startOf('week');
    while (currentDate.isBefore(end)) {
      ranges.push({
        startDate: currentDate.toDate(),
        endDate: currentDate.clone().endOf('week').toDate(),
        label: `${currentDate.format('MMM D')} - ${currentDate
          .clone()
          .endOf('week')
          .format('MMM D')}`,
      });
      currentDate.add(1, 'week');
    }
    return ranges;
  }

  // For days (default)
  const ranges: DateRange[] = [];
  const currentDate = start.clone().startOf('day');
  while (currentDate.isBefore(end)) {
    ranges.push({
      startDate: currentDate.toDate(),
      endDate: currentDate.clone().endOf('day').toDate(),
      label: currentDate.format('MMM D'),
    });
    currentDate.add(1, 'day');
  }
  return ranges;
};

// Optimized data processing function
export const processGraphData = (props: GraphDataMakerProps) => {
  const {
    dailyChartData: _dailyChartData,
    dateRange,
    convertToLocalTimezone = true,
    groupValueAggregationType = 'sum',
    dailyChartDataForAvg,
  } = props;
  const [startDate, endDate] = dateRange || [];

  if (!startDate || !endDate || _dailyChartData.length === 0) {
    return [];
  }

  // Convert dates to moments once
  const startMoment = convertToLocalTimezone
    ? moment(startDate).local()
    : moment(startDate);
  const endMoment = convertToLocalTimezone
    ? moment(endDate).local()
    : moment(endDate);

  // Calculate total days and determine optimal grouping
  const totalDays = endMoment.diff(startMoment, 'days');
  const totalWeeks = endMoment.diff(startMoment, 'weeks');

  // Create a map for quick lookups
  const dataMap = new Map<string, number>();
  _dailyChartData.forEach((point) => {
    const date = convertToLocalTimezone
      ? moment(point.X).local()
      : moment(point.X);
    const key = date.format('YYYY-MM-DD');
    dataMap.set(key, (dataMap.get(key) || 0) + point.Y);
  });

  // Determine optimal grouping based on date range
  let groupSize: 'day' | 'week' | 'month' = 'day';
  if (totalDays > 9) {
    groupSize = 'week';
  }
  if (totalWeeks >= 8) {
    groupSize = 'month';
  }

  const result: ChartDataPointWithDateRange[] = [];
  const currentMoment = startMoment.clone();

  while (currentMoment.isSameOrBefore(endMoment)) {
    let groupStart: moment.Moment;
    let groupEnd: moment.Moment;
    let groupLabel: string;

    switch (groupSize) {
      case 'day':
        groupStart = currentMoment.clone().startOf('day');
        groupEnd = currentMoment.clone().endOf('day');
        groupLabel = currentMoment.format('MMM D');
        currentMoment.add(1, 'day');
        break;
      case 'week':
        // If start of week is < date range start, start from date range start
        groupStart = currentMoment
          .clone()
          .startOf('isoWeek')
          .isBefore(startMoment)
          ? startMoment.clone()
          : currentMoment.clone().startOf('isoWeek');
        // If end of week is > date range end, end at date range end
        groupEnd = currentMoment.clone().endOf('isoWeek').isAfter(endMoment)
          ? endMoment.clone()
          : currentMoment.clone().endOf('isoWeek');
        groupLabel = groupStart.isSame(groupEnd, 'day')
          ? groupStart.format('MMM D')
          : `${groupStart.format('MMM D')} - ${groupEnd.format('MMM D')}`;
        currentMoment.add(1, 'week');
        break;
      case 'month':
        // If start of month is < date range start, start from date range start
        groupStart = currentMoment
          .clone()
          .startOf('month')
          .isBefore(startMoment)
          ? startMoment.clone()
          : currentMoment.clone().startOf('month');
        // If end of month is > date range end, end at date range end
        groupEnd = currentMoment.clone().endOf('month').isAfter(endMoment)
          ? endMoment.clone()
          : currentMoment.clone().endOf('month');
        groupLabel = currentMoment.format('MMM YYYY');
        currentMoment.add(1, 'month');
        break;
      default:
        throw new Error('Invalid group size');
    }

    // Calculate sum for the group
    let sum = 0;
    const tempMoment = groupStart.clone();
    while (tempMoment.isSameOrBefore(groupEnd)) {
      const key = tempMoment.format('YYYY-MM-DD');
      sum += dataMap.get(key) || 0;
      tempMoment.add(1, 'day');
    }

    // If group value aggregation type is avg, we need to divide the sum
    // by the sum of the daily data points.
    // FIXME: This is very confusing to maintain. I need to think of a better way to do this.
    if (groupValueAggregationType === 'avg') {
      // If dailyChartDataForAvg is not provided, we will avg on the count of days in the group.
      if (!dailyChartDataForAvg) {
        const dayCount = groupEnd.diff(groupStart, 'days') || 1;
        sum = sum / dayCount;
      } else {
        const dailySumData = dailyChartDataForAvg.filter(
          (point) =>
            moment(point.X).isSameOrAfter(groupStart, 'day') &&
            moment(point.X).isSameOrBefore(groupEnd, 'day'),
        );
        const dailySum = dailySumData.reduce((acc, point) => acc + point.Y, 0);

        if (dailySum) {
          sum = sum / dailySum;
        }
      }
    }

    result.push({
      X: groupLabel,
      Y: sum,
      dateRange: [groupStart.toDate(), groupEnd.toDate()],
    });
  }

  return result;
};

// React Hook
export const useGraphDataMakerNew = (props: GraphDataMakerProps) => {
  const chartData = useMemo(
    () => processGraphData(props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      props.dailyChartData,
      props.dateRange,
      props.convertToLocalTimezone,
      props.groupValueAggregationType,
    ],
  );

  return { chartData };
};
